import { createFileRoute, notFound } from "@tanstack/react-router";
import { LocationPageTemplate } from "@/components/LocationPageTemplate";
import { CityLocationIntro } from "@/components/CityLocationIntro";
import { getCityPageContent } from "@/data/city-page-content";
import { getLocationCity } from "@/data/location-cities";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";


export const Route = createFileRoute("/services_/managed-it-services_/$city")({
  loader: ({ params }) => {
    const city = getLocationCity(params.city);
    if (!city) throw notFound();
    return { city };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] };
    const { city } = loaderData;
    const title = city.metaTitle ?? `Managed IT Services in ${city.name}, ${city.region} | SimpleTech`;
    const description =
      city.metaDescription ?? `${city.heroSubtitle} ${city.responseTime} Call (909) 255-9112.`.slice(0, 158);

    const path = `/services/managed-it-services/${city.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        {
          name: "keywords",
          content: `managed IT services ${city.name}, IT support ${city.name} ${city.region}, ${city.name} MSP, business IT ${city.name}, IT company ${city.name}`,
        },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical(path) },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "geo.region", content: `US-${city.region}` },
        { name: "geo.placename", content: city.name },
        { name: "geo.position", content: `${city.lat};${city.lng}` },
        { name: "ICBM", content: `${city.lat}, ${city.lng}` },
      ],
      links: [{ rel: "canonical", href: canonical(path) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: `SimpleTech — Managed IT Services ${city.name}`,
            telephone: "+1-909-255-9112",
            email: "contact@simpletechhelper.com",
            url: path,
            priceRange: "$$",
            address: {
              "@type": "PostalAddress",
              streetAddress: "8880 N Benson Ave #121",
              addressLocality: "Montclair",
              addressRegion: "CA",
              postalCode: "91763",
              addressCountry: "US",
            },
            geo: { "@type": "GeoCoordinates", latitude: city.lat, longitude: city.lng },
            areaServed: { "@type": "City", name: city.name },
            aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "32" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Managed IT Services",
            name: `Managed IT Services in ${city.name}, ${city.region}`,
            url: canonical(path),
            areaServed: { "@type": "City", name: `${city.name}, ${city.region}` },
            provider: {
              "@type": "LocalBusiness",
              name: "SimpleTech",
              telephone: "+1-909-255-9112",
              url: SITE_URL,
              address: {
                "@type": "PostalAddress",
                streetAddress: "8880 N Benson Ave #121",
                addressLocality: "Montclair",
                addressRegion: "CA",
                postalCode: "91763",
                addressCountry: "US",
              },
            },
          }),
        },

        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: city.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "/" },
              { "@type": "ListItem", position: 2, name: "Services", item: "/services" },
              { "@type": "ListItem", position: 3, name: "Managed IT Services", item: "/services/managed-it-services" },
              { "@type": "ListItem", position: 4, name: city.name, item: path },
            ],
          }),
        },
      ],
    };
  },
  component: CityPage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center p-8 text-center">
      <div>
        <h1 className="text-3xl font-bold mb-2">City not found</h1>
        <p className="text-slate-600">We don't have a page for that location yet.</p>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen flex items-center justify-center p-8 text-center">
      <p className="text-slate-600">{error.message}</p>
    </div>
  ),
});

function CityPage() {
  const { city } = Route.useLoaderData();
  const content = getCityPageContent(city.slug);
  if (!content) return <LocationPageTemplate city={city} />;
  return (
    <LocationPageTemplate
      city={city}
      variant={{
        heroHeading: `Managed IT Services in ${city.name}, ${city.region}`,
        introBody: <CityLocationIntro cityName={city.name} content={content} />,
        faqs: city.faqs,
        ctaTitle: content.ctaTitle,
        ctaBody: (
          <>
            {content.ctaBody}{" "}
            <a href="tel:+19092559112" className="font-bold underline">
              Call (909) 255-9112
            </a>
            .
          </>
        ),
      }}
    />
  );
}

